import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Navbar } from '../components/layout/Navbar'
import { Stepper } from '../components/ui/Stepper'
import { Button } from '../components/ui/Button'
import { FileUpload } from '../components/ui/FileUpload'
import { authService } from '../services/authService'
import toast from 'react-hot-toast'

export const DocumentsIndividualPage = () => {
  const navigate = useNavigate()

  const [uploadedDocuments, setUploadedDocuments] = useState<Record<string, File[]>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  // A partir de la 2da solicitud del usuario, este paso es parte del
  // recorrido abreviado (sin stepper, y salta directo a la pantalla de
  // revisión en vez de la consulta de buró/CIEC/agendar visita).
  const [isRepeat, setIsRepeat] = useState(false)

  useEffect(() => {
    authService
      .isCurrentCreditApplicationTheFirst()
      .then(isFirst => setIsRepeat(!isFirst))
      .catch(() => setIsRepeat(false))
  }, [])

  const stepperSteps = [
    { id: '1', title: '', description: '', status: 'completed' as const },
    { id: '2', title: '', description: '', status: 'completed' as const },
    { id: '3', title: '', description: '', status: 'current' as const },
    { id: '4', title: '', description: '', status: 'pending' as const },
    { id: '5', title: '', description: '', status: 'pending' as const },
    { id: '6', title: '', description: '', status: 'pending' as const },
  ]

  const requiredDocuments = [
    {
      key: 'ine',
      title: 'INE (Credencial de elector)',
      description: 'Sube tu INE por ambos lados (frente y reverso)',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '🆔'
    },
    {
      key: 'curp',
      title: 'CURP',
      description: 'Documento oficial de la CURP',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '📋'
    },
    {
      key: 'rfc',
      title: 'RFC',
      description: 'Cédula de RFC actualizada',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '📄'
    },
    {
      key: 'birth_certificate',
      title: 'Acta de nacimiento',
      description: 'Acta de nacimiento certificada',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '📜'
    },
    {
      key: 'proof_of_address',
      title: 'Comprobante de domicilio',
      description: 'Recibo de servicios no mayor a 3 meses (luz, agua, teléfono, gas)',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '🏠'
    },
    {
      key: 'income_proof',
      title: 'Comprobantes de ingresos',
      description: 'Últimos 3 recibos de nómina, estados de cuenta o declaración anual',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB per file',
      icon: '💰'
    },
    {
      key: 'bank_statements',
      title: 'Estados de cuenta bancarios',
      description: 'Últimos 3 meses de estados de cuenta bancarios',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB per file',
      icon: '🏦'
    },
    {
      key: 'marriage_certificate',
      title: 'Acta de matrimonio (si aplica)',
      description: 'Solo si tu estado civil es casado(a)',
      required: false,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf'],
      maxSize: '5MB',
      icon: '💍'
    },
    {
      key: 'references',
      title: 'Referencias personales',
      description: 'Datos de contacto de 2 referencias personales (no familiares)',
      required: true,
      acceptedFormats: ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'],
      maxSize: '5MB',
      icon: '👥'
    }
  ]

  const handleFileUpload = (documentKey: string, files: File[]) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentKey]: files
    }))
  }

  const handleFileRemove = (documentKey: string, fileIndex: number) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [documentKey]: prev[documentKey]?.filter((_, index) => index !== fileIndex) || []
    }))
  }

  const validateDocuments = () => {
    const missingRequired = requiredDocuments
      .filter(doc => doc.required)
      .filter(doc => !uploadedDocuments[doc.key]?.length)

    if (missingRequired.length > 0) {
      toast.error(`Faltan documentos requeridos: ${missingRequired.map(d => d.title).join(', ')}`)
      return false
    }

    return true
  }

  const handleContinue = async () => {
    if (!validateDocuments()) {
      return
    }

    setIsSubmitting(true)

    try {
      console.log('Documents to upload:', uploadedDocuments)

      // TODO: Implement document upload service
      // const uploadResults = await documentService.uploadDocuments(uploadedDocuments, 'individual')

      console.log('Documents uploaded successfully')
      toast.success('Documentos subidos correctamente')

      // Recorrido abreviado (2da solicitud en adelante): salta directo a
      // la pantalla "en revisión" en vez de la consulta de buró/CIEC.
      const nextUrl = isRepeat
        ? '/onboarding/finished'
        : '/onboarding/bureau-check-individual'

      // Actualizar progressURL en credit-application después del éxito
      // (mismo patrón que el resto de páginas del flujo — antes faltaba
      // aquí).
      try {
        const creditAppId = await authService.getCurrentCreditApplicationId()
        if (creditAppId) {
          await authService.updateCreditApplicationProgress(
            creditAppId,
            nextUrl
          )
        }
      } catch (progressError) {
        console.error('❌ Error updating progressURL:', progressError)
        // No detener el flujo si falla la actualización del progreso
      }

      navigate(nextUrl)
    } catch (error) {
      console.error('Error uploading documents:', error)
      toast.error('Error al subir los documentos')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate('/onboarding/personal-data')
  }

  const getTotalUploadedFiles = () => {
    return Object.values(uploadedDocuments).reduce((total, files) => total + files.length, 0)
  }

  const getRequiredDocumentsCount = () => {
    return requiredDocuments.filter(doc => doc.required).length
  }

  const getUploadedRequiredCount = () => {
    return requiredDocuments
      .filter(doc => doc.required)
      .filter(doc => uploadedDocuments[doc.key]?.length > 0).length
  }

  return (
    <>
      {/* Mobile Layout */}
      <div className="min-h-screen flex flex-col md:hidden">
        <div className="h-24 flex items-center justify-center relative bg-gradient-to-b from-olr-gradient-start via-olr-gradient-middle to-olr-gradient-end">
          <img src="/assets/olr-logo.png" alt="OLR Leasing" className="filter brightness-0 invert w-24 h-auto" />
          <div className="absolute right-4">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        </div>

        <div className="flex-1 bg-olr-white p-6">
          {!isRepeat && (
            <div className="mb-8">
              <Stepper steps={stepperSteps} variant="mobile" className="justify-center" />
            </div>
          )}

          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-lg sm:text-h3 text-olr-dark mb-3 sm:mb-4 font-semibold">
              Documentación personal
            </h1>
            <p className="text-sm sm:text-paragraph-2 text-olr-body leading-relaxed">
              Sube los documentos requeridos para verificar tu identidad e ingresos. 
              Asegúrate de que sean legibles y estén vigentes.
            </p>
          </div>

          {/* Progress indicator */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-800">Progreso de documentos</span>
              <span className="text-sm text-blue-600">
                {getUploadedRequiredCount()}/{getRequiredDocumentsCount()} requeridos
              </span>
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(getUploadedRequiredCount() / getRequiredDocumentsCount()) * 100}%` }}
              ></div>
            </div>
            {getTotalUploadedFiles() > 0 && (
              <p className="text-xs text-blue-600 mt-1">
                Total de archivos: {getTotalUploadedFiles()}
              </p>
            )}
          </div>

          <div className="space-y-6">
            {requiredDocuments.map((doc) => (
              <div key={doc.key} className="bg-gray-50 p-4 rounded-lg border">
                <div className="flex items-start space-x-3 mb-4">
                  <span className="text-2xl">{doc.icon}</span>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-base font-semibold text-olr-dark">{doc.title}</h3>
                      {doc.required && (
                        <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded-full">
                          Requerido
                        </span>
                      )}
                      {uploadedDocuments[doc.key]?.length > 0 && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          ✓ {uploadedDocuments[doc.key].length} archivo(s)
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-olr-body mt-1">{doc.description}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Formatos: {doc.acceptedFormats.join(', ')} • Max: {doc.maxSize}
                    </p>
                  </div>
                </div>

                <FileUpload
                  onFileUpload={(files) => handleFileUpload(doc.key, files)}
                  onFileRemove={(fileIndex) => handleFileRemove(doc.key, fileIndex)}
                  uploadedFiles={uploadedDocuments[doc.key] || []}
                  acceptedFormats={doc.acceptedFormats}
                  maxSize={5 * 1024 * 1024} // 5MB in bytes
                  multiple={doc.key === 'income_proof' || doc.key === 'bank_statements' || doc.key === 'ine'}
                  className="w-full"
                />
              </div>
            ))}
          </div>

          <div className="flex flex-col space-y-3 sm:space-y-4 pt-8">
            <Button
              type="button"
              variant="primary"
              onClick={handleContinue}
              className="w-full"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Subiendo documentos...' : 'Continuar'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleCancel}
              className="w-full"
            >
              Regresar
            </Button>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden md:flex min-h-screen flex-col">
        <Navbar />
        <div
          className="flex-1 flex items-center justify-center py-12"
          style={{
            backgroundImage: 'url(/assets/bg-flow.svg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="max-w-6xl w-full mx-8 px-16 py-12">
            {!isRepeat && (
              <div className="mb-12">
                <Stepper steps={stepperSteps} variant="desktop" orientation="horizontal" className="justify-center" />
              </div>
            )}

            <div className="text-center mb-10">
              <h1 className="text-h2 text-olr-dark mb-6">Documentación personal</h1>
              <p className="text-paragraph-1 text-olr-body max-w-4xl mx-auto">
                Sube los documentos requeridos para verificar tu identidad e ingresos. 
                Asegúrate de que sean legibles y estén vigentes.
              </p>
            </div>

            {/* Progress indicator */}
            <div className="mb-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-lg font-medium text-blue-800">Progreso de documentos</span>
                <span className="text-base text-blue-600">
                  {getUploadedRequiredCount()}/{getRequiredDocumentsCount()} documentos requeridos
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-3">
                <div 
                  className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                  style={{ width: `${(getUploadedRequiredCount() / getRequiredDocumentsCount()) * 100}%` }}
                ></div>
              </div>
              {getTotalUploadedFiles() > 0 && (
                <p className="text-sm text-blue-600 mt-2">
                  Total de archivos subidos: {getTotalUploadedFiles()}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {requiredDocuments.map((doc) => (
                <div key={doc.key} className="bg-white p-6 rounded-lg border shadow-sm">
                  <div className="flex items-start space-x-4 mb-6">
                    <span className="text-3xl">{doc.icon}</span>
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold text-olr-dark">{doc.title}</h3>
                        {doc.required && (
                          <span className="text-sm bg-red-100 text-red-800 px-3 py-1 rounded-full">
                            Requerido
                          </span>
                        )}
                        {uploadedDocuments[doc.key]?.length > 0 && (
                          <span className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                            ✓ {uploadedDocuments[doc.key].length} archivo(s)
                          </span>
                        )}
                      </div>
                      <p className="text-base text-olr-body mb-2">{doc.description}</p>
                      <p className="text-sm text-gray-500">
                        Formatos: {doc.acceptedFormats.join(', ')} • Tamaño máximo: {doc.maxSize}
                      </p>
                    </div>
                  </div>

                  <FileUpload
                    onFileUpload={(files) => handleFileUpload(doc.key, files)}
                    onFileRemove={(fileIndex) => handleFileRemove(doc.key, fileIndex)}
                    uploadedFiles={uploadedDocuments[doc.key] || []}
                    acceptedFormats={doc.acceptedFormats}
                    maxSize={5 * 1024 * 1024} // 5MB in bytes
                    multiple={doc.key === 'income_proof' || doc.key === 'bank_statements' || doc.key === 'ine'}
                    className="w-full"
                  />
                </div>
              ))}
            </div>

            <div className="flex justify-center space-x-6 pt-12">
              <Button type="button" variant="secondary" onClick={handleCancel}>
                Regresar
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleContinue}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Subiendo documentos...' : 'Continuar'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}